import { useRef, useEffect, useCallback } from "react";
import type { MedplumClient } from "@medplum/core";
import type { UseFormReturnType } from "@mantine/form";
import type { Patient, Practitioner, Composition } from "@medplum/fhirtypes";
import type {
  CallCompositionOption,
  EncounterBundleInput,
  ScribeFormShape,
} from "@benchmark/core/types";
import {
  searchAllIntakeCompositions,
  type CompositionWithEncounter,
} from "@benchmark/core/services";
import { compositionSearchEnvOptions } from "@/utils/fhir/composition-runtime-config";
import { compositionToEncounterFormData } from "@benchmark/core/adapters";
import { trackAction, TelemetryActions } from "@/app/boot/telemetry";
import {
  DomainEventNames,
  globalEventEmitter,
  type UserErrorShownEvent,
} from "@benchmark/core/events";

/**
 * Minimum loader display time in ms.
 * @agentic connections
 */
export const MIN_SCRIBE_LOADER_MS = 900;

/**
 * Delay between scribe polling attempts in ms.
 * @agentic connections
 */
export const SCRIBE_ATTEMPT_DELAY_MS = 12 * 1000;

/**
 * Maximum scribe polling attempts.
 * @agentic connections
 */
export const SCRIBE_MAX_ATTEMPTS = 8;

/**
 * Extension URL for call duration on Composition.
 * @agentic connections
 */
const CALL_DURATION_EXTENSION_URL =
  "https://benchmark.health/fhir/StructureDefinition/call-duration-ms";

/**
 * Scribe prefill polling/application status.
 * @agentic connections
 */
export type ScribeStatus =
  "idle" | "loading" | "found-polling" | "found-complete" | "applied" | "error";

/**
 * Props for the scribe prefill runner component. Generic over the form
 * shape so intake forms can pass their typed `useForm<T>` instance
 * directly without `as unknown as` casts. The constraint
 * `T extends ScribeFormShape` enforces that the form owns the fields the
 * runner reads / writes (`_scribeApplied`, `_scribeCompositionId`,
 * `_scribeEncounterId`).
 *
 * @agentic connections
 */
export type ScribePrefillRunnerProps<T extends ScribeFormShape> = {
  shouldStart: boolean;
  medplum: MedplumClient;
  patient: Patient;
  profile: Practitioner;
  form: UseFormReturnType<T>;
  applyScribePrefill: (prefill: Partial<EncounterBundleInput>) => void;
  setScribeLoading: (loading: boolean) => void;
  currentEncounterId?: string;
  onStatusChange?: (status: ScribeStatus) => void;
  runKey?: number;
  /** Callback when compositions are found/updated */
  onCompositionsFound?: (compositions: CallCompositionOption[]) => void;
  /** Currently selected composition ID (for multi-composition mode) */
  selectedCompositionId?: string | null;
};

/**
 * Extracts call duration from Composition extensions.
 * @agentic connections
 */
const getCompositionDuration = (
  composition: Composition,
): number | undefined => {
  const ext = composition.extension?.find(
    (e) => e.url === CALL_DURATION_EXTENSION_URL,
  );
  return ext?.valueInteger;
};

/**
 * Extracts author display name from Composition.
 * @agentic connections
 */
const getCompositionAuthorDisplay = (
  composition: Composition,
): string | undefined => {
  // First author is typically the practitioner
  const author = composition.author?.[0];
  return author?.display;
};

/**
 * Extension URL for Dialpad call ID on Composition.
 * @agentic connections
 */
const DIALPAD_CALL_ID_EXTENSION_URL =
  "https://benchmark.health/fhir/StructureDefinition/dialpad-call-id";

/**
 * Extracts Dialpad call ID from Composition extensions.
 * @agentic connections
 */
const getCompositionDialpadCallId = (
  composition: Composition,
): string | undefined => {
  const ext = composition.extension?.find(
    (e) => e.url === DIALPAD_CALL_ID_EXTENSION_URL,
  );
  return ext?.valueString;
};

/**
 * Converts CompositionWithEncounter to CallCompositionOption for display.
 * @agentic connections
 */
const toCallCompositionOption = (
  result: CompositionWithEncounter,
  existingIds: Set<string>,
): CallCompositionOption => ({
  compositionId: result.composition.id!,
  encounterId: result.encounter?.id,
  date: result.composition.date || new Date().toISOString(),
  durationMs: getCompositionDuration(result.composition),
  authorDisplay: getCompositionAuthorDisplay(result.composition),
  isNew: !existingIds.has(result.composition.id!),
  dialpadCallId: getCompositionDialpadCallId(result.composition),
});

/**
 * Step-scoped runner that triggers scribe prefill when Notes step mounts.
 * @agentic connections
 */
export const ScribePrefillRunner = <T extends ScribeFormShape>({
  shouldStart,
  medplum,
  patient,
  profile,
  form,
  applyScribePrefill,
  setScribeLoading,
  currentEncounterId,
  onStatusChange,
  runKey,
  onCompositionsFound,
  selectedCompositionId,
}: ScribePrefillRunnerProps<T>) => {
  const runTokenRef = useRef(0);
  const currentEncounterIdRef = useRef<string | undefined>(currentEncounterId);
  const foundCompositionIdsRef = useRef<Set<string>>(new Set());
  const compositionsRef = useRef<CompositionWithEncounter[]>([]);
  // Start time of the active poll run, for `applied` latency measurement.
  const pollStartMsRef = useRef<number | null>(null);

  const contextRef = useRef({
    form,
    applyScribePrefill,
    setScribeLoading,
    onStatusChange,
    onCompositionsFound,
  });

  // Keep a stable ref for the current encounter id to avoid effect resets
  useEffect(() => {
    currentEncounterIdRef.current = currentEncounterId;
  }, [currentEncounterId]);

  useEffect(() => {
    contextRef.current = {
      form,
      applyScribePrefill,
      setScribeLoading,
      onStatusChange,
      onCompositionsFound,
    };
  }, [
    form,
    applyScribePrefill,
    setScribeLoading,
    onStatusChange,
    onCompositionsFound,
  ]);

  // Apply a specific composition's data to the form
  const applyCompositionPrefill = useCallback((compositionId: string) => {
    const result = compositionsRef.current.find(
      (c) => c.composition.id === compositionId,
    );
    if (!result) return;

    const {
      form: formCtx,
      applyScribePrefill: applyPrefillCtx,
      onStatusChange: onStatusCtx,
    } = contextRef.current;

    if (import.meta.env?.MODE !== "production") {
      console.info("[EncounterForm] (Notes) Scribe prefill: applying", {
        compositionId: result.composition.id,
        encounterId: result.encounter?.id,
      });
    }

    // Set composition/encounter IDs on form
    if (result.composition.id) {
      formCtx.setFieldValue(
        "_scribeCompositionId" as never,
        result.composition.id as never,
      );
    }
    formCtx.setFieldValue(
      "_scribeEncounterId" as never,
      (result.encounter?.id ?? "") as never,
    );

    // Check encounter ID mismatch
    const currentId = currentEncounterIdRef.current;
    if (
      result.encounter?.id &&
      currentId &&
      result.encounter.id !== currentId
    ) {
      return;
    }

    // Apply the prefill data
    const prefill = compositionToEncounterFormData(
      result.composition,
      result.encounter,
    );
    applyPrefillCtx(prefill as Partial<EncounterBundleInput>);

    const gotTiming = Boolean(prefill.period?.start);
    const gotNotes = Object.keys(prefill).some((k) => k !== "period");
    if (gotTiming || gotNotes) {
      formCtx.setFieldValue("_scribeApplied" as never, true as never);
    }

    onStatusCtx?.("applied");
    trackAction(TelemetryActions.ScribeApplied, {
      latencyMs: pollStartMsRef.current
        ? Date.now() - pollStartMsRef.current
        : undefined,
    });
  }, []);

  // Handle external request to apply a composition
  useEffect(() => {
    if (selectedCompositionId) {
      applyCompositionPrefill(selectedCompositionId);
    }
  }, [selectedCompositionId, applyCompositionPrefill]);

  // Main polling effect
  useEffect(() => {
    if (!shouldStart) return;
    const token = ++runTokenRef.current;
    const {
      form: formCtx,
      setScribeLoading: setLoadingCtx,
      onStatusChange: onStatusCtx,
      onCompositionsFound: onCompositionsFoundCtx,
    } = contextRef.current;

    // Reset tracking for new run
    foundCompositionIdsRef.current = new Set();
    compositionsRef.current = [];

    // One AuditEvent per fetch run that ends without a call summary, so
    // wait-and-re-fetch frequency is countable (`fetchNumber` > 1 = re-fetch).
    const runStart = Date.now();
    let completedAttempts = 0;
    let settled = false;
    const recordEmptyFetch = (
      endReason: "timeout" | "abandoned" | "error",
    ): void => {
      if (settled) return;
      settled = true;
      // Zero completed polls = StrictMode remount or instant navigation.
      if (endReason === "abandoned" && completedAttempts === 0) return;
      globalEventEmitter.publish<UserErrorShownEvent>(
        DomainEventNames.UserErrorShown,
        {
          source: "call-summary-empty",
          severity: "minor",
          error: "No AICD call summary found",
          detail: {
            endReason,
            attempts: completedAttempts,
            elapsedMs: Date.now() - runStart,
            fetchNumber: (runKey ?? 0) + 1,
          },
        },
      );
    };

    (async () => {
      if (import.meta.env?.MODE !== "production") {
        console.info(
          "[EncounterForm] (Notes) Scribe prefill: starting lookup",
          {
            scribeApplied: formCtx.values._scribeApplied,
            patientId: patient.id,
            practitionerId: profile.id,
          },
        );
      }

      setLoadingCtx(true);
      onStatusCtx?.("loading");
      const loaderStart = Date.now();
      pollStartMsRef.current = loaderStart;
      trackAction(TelemetryActions.ScribeStarted, {
        patientId: patient.id,
        practitionerId: profile.id,
      });

      try {
        // If already applied with a known composition id, skip
        if (
          formCtx.values._scribeApplied &&
          formCtx.values._scribeCompositionId
        ) {
          if (import.meta.env?.MODE !== "production") {
            console.info(
              "[EncounterForm] (Notes) Scribe prefill: already applied, skipping fetch",
              {
                compositionId: formCtx.values._scribeCompositionId,
              },
            );
          }
          settled = true;
          onStatusCtx?.("applied");
          return;
        }

        // Polling loop - continues even after finding compositions
        for (let attempt = 0; attempt < SCRIBE_MAX_ATTEMPTS; attempt += 1) {
          // Check for cancellation
          if (!Object.is(runTokenRef.current, token)) return;

          // Wait between attempts (except first)
          if (attempt > 0) {
            await new Promise((resolve) =>
              setTimeout(resolve, SCRIBE_ATTEMPT_DELAY_MS),
            );
            if (!Object.is(runTokenRef.current, token)) return;
          }

          trackAction(TelemetryActions.ScribeAttempt, {
            attempt: attempt + 1,
            maxAttempts: SCRIBE_MAX_ATTEMPTS,
            elapsedMs: Date.now() - loaderStart,
          });

          // Search for all compositions
          const results = await searchAllIntakeCompositions(
            medplum,
            patient,
            profile,
            {
              ...compositionSearchEnvOptions(),
              lookbackMs: 24 * 60 * 60 * 1000,
            },
          );

          if (!Object.is(runTokenRef.current, token)) return;
          completedAttempts += 1;

          // Check for new compositions
          const previousIds = foundCompositionIdsRef.current;
          const currentIds = new Set(
            results.map((r) => r.composition.id).filter(Boolean) as string[],
          );

          // Detect if we have new compositions
          const hasNewCompositions = [...currentIds].some(
            (id) => !previousIds.has(id),
          );

          if (results.length > 0) {
            settled = true;
            // Convert to options, marking new ones
            const options = results
              .filter((r) => r.composition.id)
              .map((r) => toCallCompositionOption(r, previousIds));

            // Update refs
            foundCompositionIdsRef.current = currentIds;
            compositionsRef.current = results;

            // Notify parent of found compositions
            onCompositionsFoundCtx?.(options);

            const isLastAttempt = attempt === SCRIBE_MAX_ATTEMPTS - 1;
            onStatusCtx?.(isLastAttempt ? "found-complete" : "found-polling");

            if (hasNewCompositions) {
              trackAction(TelemetryActions.ScribeCompositionsFound, {
                count: results.length,
                attempt: attempt + 1,
                latencyMs: Date.now() - loaderStart,
              });
            }

            if (import.meta.env?.MODE !== "production") {
              console.info(
                "[EncounterForm] (Notes) Scribe prefill: found compositions",
                {
                  count: results.length,
                  hasNew: hasNewCompositions,
                  attempt: attempt + 1,
                  maxAttempts: SCRIBE_MAX_ATTEMPTS,
                },
              );
            }
          } else if (attempt === SCRIBE_MAX_ATTEMPTS - 1) {
            // No compositions found after all attempts
            onStatusCtx?.("error");
            trackAction(TelemetryActions.ScribeTimeout, {
              attempts: SCRIBE_MAX_ATTEMPTS,
              elapsedMs: Date.now() - loaderStart,
            });
            recordEmptyFetch("timeout");
          }
        }
      } catch (err) {
        recordEmptyFetch("error");
        throw err;
      } finally {
        const elapsed = Date.now() - loaderStart;
        const delay = Math.max(0, MIN_SCRIBE_LOADER_MS - elapsed);
        setTimeout(() => {
          if (Object.is(runTokenRef.current, token)) {
            setLoadingCtx(false);
          }
        }, delay);
      }
    })();

    // Cleanup cancels current run by invalidating token
    return () => {
      runTokenRef.current += 1;
      recordEmptyFetch("abandoned");
    };
  }, [shouldStart, runKey, medplum, patient, profile]);

  return null;
};
